import React, { useEffect, useMemo, useRef, useState } from 'react';
import './HoverRevealScrollbarWrapper.css';

const scrollbarArrowSize = 14;
const activeScrollbarEventName = 'hoverRevealScrollbar:active';
const scrollbarHideDelayMs = 300;

const HoverRevealScrollbarWrapper = ({ children, className = '', makeSelfScrollable = false, size = 8 }) => {
  const instanceIdReference = useRef(`hover-reveal-${Math.random().toString(36).slice(2)}`);
  const wrapperReference = useRef(null);
  const scrollElementReference = useRef(null);
  const dragStateReference = useRef({ axis: null, startClient: 0, startScroll: 0 });
  const hideTimeoutReference = useRef(null);
  const isTrackHoveredReference = useRef(false);
  const isWrapperHoveredReference = useRef(false);
  const [isScrollbarVisible, setIsScrollbarVisible] = useState(false);
  const [scrollState, setScrollState] = useState({
    verticalThumbSize: 0,
    verticalThumbOffset: 0,
    horizontalThumbSize: 0,
    horizontalThumbOffset: 0,
    hasVerticalScroll: false,
    hasHorizontalScroll: false,
  });

  const updateScrollMetrics = () => {
    const scrollElement = scrollElementReference.current;
    if (!scrollElement) return;

    const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = scrollElement;
    const hasVerticalScroll = scrollHeight > clientHeight + 1;
    const hasHorizontalScroll = scrollWidth > clientWidth + 1;

    const verticalThumbSize = hasVerticalScroll ? Math.max((clientHeight * clientHeight) / scrollHeight, 32) : 0;
    const horizontalThumbSize = hasHorizontalScroll ? Math.max((clientWidth * clientWidth) / scrollWidth, 32) : 0;

    const verticalTrackSize = Math.max(clientHeight - scrollbarArrowSize * 2, 0);
    const horizontalTrackSize = Math.max(clientWidth - scrollbarArrowSize * 2, 0);
    const verticalMovableSpace = Math.max(verticalTrackSize - verticalThumbSize, 0);
    const horizontalMovableSpace = Math.max(horizontalTrackSize - horizontalThumbSize, 0);

    const verticalMaxScroll = Math.max(scrollHeight - clientHeight, 1);
    const horizontalMaxScroll = Math.max(scrollWidth - clientWidth, 1);

    const verticalThumbOffset = hasVerticalScroll ? scrollbarArrowSize + (scrollTop / verticalMaxScroll) * verticalMovableSpace : 0;
    const horizontalThumbOffset = hasHorizontalScroll ? scrollbarArrowSize + (scrollLeft / horizontalMaxScroll) * horizontalMovableSpace : 0;

    setScrollState({
      verticalThumbSize,
      verticalThumbOffset,
      horizontalThumbSize,
      horizontalThumbOffset,
      hasVerticalScroll,
      hasHorizontalScroll,
    });
  };

  const clearHideTimeout = () => {
    if (!hideTimeoutReference.current) return;
    window.clearTimeout(hideTimeoutReference.current);
    hideTimeoutReference.current = null;
  };

  const showScrollbarTemporarily = (delay = scrollbarHideDelayMs, force = false) => {
    if (!force && !isWrapperHoveredReference.current && !dragStateReference.current.axis) return;
    window.dispatchEvent(new CustomEvent(activeScrollbarEventName, { detail: { instanceId: instanceIdReference.current } }));
    setIsScrollbarVisible(true);
    clearHideTimeout();
    hideTimeoutReference.current = window.setTimeout(() => {
      if (isTrackHoveredReference.current || dragStateReference.current.axis) return;
      setIsScrollbarVisible(false);
    }, delay);
  };

  useEffect(() => {
    const handleActiveScrollbarChange = (event) => {
      const activeInstanceId = event?.detail?.instanceId;
      if (!activeInstanceId || activeInstanceId === instanceIdReference.current) return;
      clearHideTimeout();
      setIsScrollbarVisible(false);
    };

    window.addEventListener(activeScrollbarEventName, handleActiveScrollbarChange);
    return () => {
      window.removeEventListener(activeScrollbarEventName, handleActiveScrollbarChange);
    };
  }, []);

  useEffect(() => {
    const wrapperElement = wrapperReference.current;
    if (!wrapperElement) return;

    const resolvedScrollElement = makeSelfScrollable ? wrapperElement : wrapperElement.querySelector("[data-hover-scrollable='true']");
    if (!resolvedScrollElement) return;

    scrollElementReference.current = resolvedScrollElement;
    updateScrollMetrics();

    const handleScroll = () => {
      updateScrollMetrics();
      showScrollbarTemporarily();
    };
    const handleWheel = () => showScrollbarTemporarily();
    const resizeObserver = new ResizeObserver(() => updateScrollMetrics());

    resolvedScrollElement.addEventListener('scroll', handleScroll, { passive: true });
    resolvedScrollElement.addEventListener('wheel', handleWheel, { passive: true });
    resizeObserver.observe(resolvedScrollElement);

    return () => {
      resolvedScrollElement.removeEventListener('scroll', handleScroll);
      resolvedScrollElement.removeEventListener('wheel', handleWheel);
      resizeObserver.disconnect();
    };
  }, [children, makeSelfScrollable]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const scrollElement = scrollElementReference.current;
      const { axis, startClient, startScroll } = dragStateReference.current;
      if (!scrollElement || !axis) return;

      if (axis === 'vertical') {
        const totalScrollable = scrollElement.scrollHeight - scrollElement.clientHeight;
        const verticalTrackSize = Math.max(scrollElement.clientHeight - scrollbarArrowSize * 2, 0);
        const trackScrollable = verticalTrackSize - scrollState.verticalThumbSize;
        if (trackScrollable <= 0) return;
        const movement = event.clientY - startClient;
        const nextScrollTop = startScroll + (movement * totalScrollable) / trackScrollable;
        scrollElement.scrollTop = nextScrollTop;
      } else {
        const totalScrollable = scrollElement.scrollWidth - scrollElement.clientWidth;
        const horizontalTrackSize = Math.max(scrollElement.clientWidth - scrollbarArrowSize * 2, 0);
        const trackScrollable = horizontalTrackSize - scrollState.horizontalThumbSize;
        if (trackScrollable <= 0) return;
        const movement = event.clientX - startClient;
        const nextScrollLeft = startScroll + (movement * totalScrollable) / trackScrollable;
        scrollElement.scrollLeft = nextScrollLeft;
      }
    };

    const handlePointerUp = () => {
      dragStateReference.current = { axis: null, startClient: 0, startScroll: 0 };
      document.body.classList.remove('hoverRevealScrollbarDragging');
      showScrollbarTemporarily(scrollbarHideDelayMs);
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
    };
  }, [scrollState.horizontalThumbSize, scrollState.verticalThumbSize]);

  useEffect(() => {
    return () => {
      clearHideTimeout();
    };
  }, []);

  const startVerticalDrag = (event) => {
    const scrollElement = scrollElementReference.current;
    if (!scrollElement) return;
    event.preventDefault();
    dragStateReference.current = { axis: 'vertical', startClient: event.clientY, startScroll: scrollElement.scrollTop };
    document.body.classList.add('hoverRevealScrollbarDragging');
    setIsScrollbarVisible(true);
  };

  const startHorizontalDrag = (event) => {
    const scrollElement = scrollElementReference.current;
    if (!scrollElement) return;
    event.preventDefault();
    dragStateReference.current = { axis: 'horizontal', startClient: event.clientX, startScroll: scrollElement.scrollLeft };
    document.body.classList.add('hoverRevealScrollbarDragging');
    setIsScrollbarVisible(true);
  };

  const scrollByOffset = (axis, offset) => {
    const scrollElement = scrollElementReference.current;
    if (!scrollElement) return;

    if (axis === 'vertical') {
      scrollElement.scrollTop += offset;
      return;
    }

    scrollElement.scrollLeft += offset;
  };

  const jumpToTrackPosition = (axis, event) => {
    const scrollElement = scrollElementReference.current;
    if (!scrollElement) return;

    const trackRectangle = event.currentTarget.getBoundingClientRect();

    if (axis === 'vertical') {
      const totalScrollable = scrollElement.scrollHeight - scrollElement.clientHeight;
      const verticalTrackSize = Math.max(scrollElement.clientHeight - scrollbarArrowSize * 2, 0);
      const movableSpace = Math.max(verticalTrackSize - scrollState.verticalThumbSize, 0);
      if (movableSpace <= 0 || totalScrollable <= 0) return;

      const clickedOffset = event.clientY - trackRectangle.top - scrollbarArrowSize - scrollState.verticalThumbSize / 2;
      const clampedOffset = Math.min(Math.max(clickedOffset, 0), movableSpace);
      scrollElement.scrollTop = (clampedOffset / movableSpace) * totalScrollable;
      return;
    }

    const totalScrollable = scrollElement.scrollWidth - scrollElement.clientWidth;
    const horizontalTrackSize = Math.max(scrollElement.clientWidth - scrollbarArrowSize * 2, 0);
    const movableSpace = Math.max(horizontalTrackSize - scrollState.horizontalThumbSize, 0);
    if (movableSpace <= 0 || totalScrollable <= 0) return;

    const clickedOffset = event.clientX - trackRectangle.left - scrollbarArrowSize - scrollState.horizontalThumbSize / 2;
    const clampedOffset = Math.min(Math.max(clickedOffset, 0), movableSpace);
    scrollElement.scrollLeft = (clampedOffset / movableSpace) * totalScrollable;
  };

  const combinedClassName = `hoverRevealScrollbarWrapper ${makeSelfScrollable ? 'hoverRevealScrollbarWrapperSelf' : ''} ${className}`.trim();
  const resolvedSize = Number.isFinite(Number(size)) ? Math.max(Number(size), 6) : 8;
  const verticalThumbStyle = useMemo(
    () => ({ height: `${scrollState.verticalThumbSize}px`, transform: `translateY(${scrollState.verticalThumbOffset}px)` }),
    [scrollState.verticalThumbOffset, scrollState.verticalThumbSize],
  );
  const horizontalThumbStyle = useMemo(
    () => ({ width: `${scrollState.horizontalThumbSize}px`, transform: `translateX(${scrollState.horizontalThumbOffset}px)` }),
    [scrollState.horizontalThumbOffset, scrollState.horizontalThumbSize],
  );

  return (
    <div
      ref={wrapperReference}
      className={`${combinedClassName} ${isScrollbarVisible ? 'hoverRevealScrollbarWrapperVisible' : ''}`.trim()}
      style={{ '--hover-reveal-scrollbar-size': `${resolvedSize}px` }}
      onMouseEnter={() => {
        isWrapperHoveredReference.current = true;
      }}
      onMouseLeave={() => {
        isWrapperHoveredReference.current = false;
        if (!dragStateReference.current.axis && !isTrackHoveredReference.current) {
          clearHideTimeout();
          setIsScrollbarVisible(false);
        }
      }}
    >
      {children}
      {scrollState.hasVerticalScroll ? (
        <div
          className={`hoverRevealScrollbarTrack hoverRevealScrollbarTrackVertical ${
            scrollState.hasHorizontalScroll ? 'hoverRevealScrollbarTrackVerticalInset' : ''
          }`.trim()}
          onMouseDown={(event) => jumpToTrackPosition('vertical', event)}
          onMouseEnter={() => {
            isTrackHoveredReference.current = true;
            clearHideTimeout();
            window.dispatchEvent(new CustomEvent(activeScrollbarEventName, { detail: { instanceId: instanceIdReference.current } }));
            setIsScrollbarVisible(true);
          }}
          onMouseLeave={() => {
            isTrackHoveredReference.current = false;
            showScrollbarTemporarily(scrollbarHideDelayMs, true);
          }}
        >
          <button type='button' className='hoverRevealScrollbarArrow hoverRevealScrollbarArrowUp' onMouseDown={(event) => event.stopPropagation()} onClick={() => scrollByOffset('vertical', -56)} aria-label='Scroll up' />
          <div className='hoverRevealScrollbarThumb hoverRevealScrollbarThumbVertical' style={verticalThumbStyle} onMouseDown={(event) => {
            event.stopPropagation();
            startVerticalDrag(event);
          }} />
          <button type='button' className='hoverRevealScrollbarArrow hoverRevealScrollbarArrowDown' onMouseDown={(event) => event.stopPropagation()} onClick={() => scrollByOffset('vertical', 56)} aria-label='Scroll down' />
        </div>
      ) : null}
      {scrollState.hasHorizontalScroll ? (
        <div
          className={`hoverRevealScrollbarTrack hoverRevealScrollbarTrackHorizontal ${
            scrollState.hasVerticalScroll ? 'hoverRevealScrollbarTrackHorizontalInset' : ''
          }`.trim()}
          onMouseDown={(event) => jumpToTrackPosition('horizontal', event)}
          onMouseEnter={() => {
            isTrackHoveredReference.current = true;
            clearHideTimeout();
            window.dispatchEvent(new CustomEvent(activeScrollbarEventName, { detail: { instanceId: instanceIdReference.current } }));
            setIsScrollbarVisible(true);
          }}
          onMouseLeave={() => {
            isTrackHoveredReference.current = false;
            showScrollbarTemporarily(scrollbarHideDelayMs, true);
          }}
        >
          <button type='button' className='hoverRevealScrollbarArrow hoverRevealScrollbarArrowLeft' onMouseDown={(event) => event.stopPropagation()} onClick={() => scrollByOffset('horizontal', -72)} aria-label='Scroll left' />
          <div className='hoverRevealScrollbarThumb hoverRevealScrollbarThumbHorizontal' style={horizontalThumbStyle} onMouseDown={(event) => {
            event.stopPropagation();
            startHorizontalDrag(event);
          }} />
          <button type='button' className='hoverRevealScrollbarArrow hoverRevealScrollbarArrowRight' onMouseDown={(event) => event.stopPropagation()} onClick={() => scrollByOffset('horizontal', 72)} aria-label='Scroll right' />
        </div>
      ) : null}
    </div>
  );
};

export default HoverRevealScrollbarWrapper;
